import { forwardRef, Popover, StyleProps } from '@chakra-ui/react';
import { PropsWithChildren, ReactElement } from 'react';

import { CalendarOutline } from '@bonusly/ds-icon';

import { useMonthPicker } from '../../hooks/use-month-picker';
import { DateValue } from '../../types';
import { MonthSelectorContext } from './month-selector-context';
import { MonthSelectorControls } from './month-selector-controls';
import { MonthSelectorGrid } from './month-selector-grid';
import { MonthSelectorInput } from './month-selector-input';
import { MonthSelectorPopup } from './month-selector-popup';

export type MonthSelectorProps = PropsWithChildren<
	StyleProps & {
		disableMonthIf?: (month: Date) => boolean;
		displayFormat?: 'short' | 'long';
		isClearable?: boolean;
		isDisabled?: boolean;
		leftIcon?: ReactElement;
		onChange: (value: DateValue) => void;
		value: DateValue;
	}
>;

export const MonthSelector = forwardRef<MonthSelectorProps, 'button'>(
	(
		{ children, disableMonthIf, displayFormat = 'long', isClearable = true, isDisabled = false, leftIcon = <CalendarOutline />, onChange, value, ...rest },
		ref
	) => {
		const picker = useMonthPicker({ disableMonthIf, onChange, value });

		if (children) {
			return children;
		}

		return (
			<MonthSelectorContext.Provider
				value={{
					displayFormat,
					isClearable,
					isDisabled,
					value,
					...picker,
				}}
			>
				<Popover isLazy isOpen={picker.isOpen} onClose={picker.onClose}>
					<MonthSelectorInput leftIcon={leftIcon} ref={ref} {...rest} />

					<MonthSelectorPopup p={4}>
						<MonthSelectorControls mb={2} />
						<MonthSelectorGrid />
					</MonthSelectorPopup>
				</Popover>
			</MonthSelectorContext.Provider>
		);
	}
);

MonthSelector.displayName = 'MonthSelector';
